/**
 * p-values from a test statistic: z, t, χ², F, or Pearson's r.
 *
 * Every tail comes from `@/lib/stats/distributions`, which computes upper tails directly
 * rather than as 1 − CDF, so a p of 3 × 10⁻¹² is reported as that and not as zero.
 */

import { chiSquarePdf, fPdf, studentTPdf } from "@/lib/stats/densities";
import {
  chiSquareCdf,
  chiSquareQuantile,
  chiSquareUpper,
  fCdf,
  fQuantile,
  fUpper,
  normalCdf,
  normalPdf,
  normalUpper,
  studentTCdf,
  studentTUpper,
} from "@/lib/stats/distributions";
import {
  apaPText,
  fixed,
  formatDf,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-distributions/format";

export type PStatistic = "z" | "t" | "chi2" | "f" | "r";
export type Tail = "left" | "right" | "two";

export interface PValueInput {
  statistic: PStatistic;
  value: number;
  /** t and χ². */
  df?: number;
  /** F numerator and denominator. */
  df1?: number;
  df2?: number;
  /** Sample size, for r. */
  n?: number;
  tail: Tail;
}

/** Which input is wrong — the widget maps each to a sentence. */
export type PValueProblem = "df" | "df1" | "df2" | "n" | "negative" | "rRange";

export interface PValueResult {
  p: number;
  /** The distribution the p-value is read from (r is tested as t). */
  distribution: "z" | "t" | "chi2" | "f";
  /** The statistic on that distribution: r converted to t, otherwise the input. */
  testValue: number;
  df?: number;
  df1?: number;
  df2?: number;
  /** P(X ≤ x) and P(X ≥ x). */
  lower: number;
  upper: number;
}

function validDf(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value > 0;
}

export function checkPValueInput(input: PValueInput): PValueProblem | null {
  const { statistic, value } = input;
  if ((statistic === "t" || statistic === "chi2") && !validDf(input.df)) {
    return "df";
  }
  if (statistic === "f") {
    if (!validDf(input.df1)) {
      return "df1";
    }
    if (!validDf(input.df2)) {
      return "df2";
    }
  }
  if ((statistic === "chi2" || statistic === "f") && value < 0) {
    return "negative";
  }
  if (statistic === "r") {
    if (!(Math.abs(value) < 1)) {
      return "rRange";
    }
    if (input.n === undefined || !Number.isInteger(input.n) || input.n < 3) {
      return "n";
    }
  }
  return null;
}

/** t = r √((n − 2) / (1 − r²)), tested on n − 2 df. */
export function rToT(r: number, n: number): number {
  return r * Math.sqrt((n - 2) / (1 - r * r));
}

function pick(tail: Tail, lower: number, upper: number): number {
  if (tail === "left") {
    return lower;
  }
  if (tail === "right") {
    return upper;
  }
  return Math.min(1, 2 * Math.min(lower, upper));
}

export function computePValue(input: PValueInput): PValueResult | null {
  if (checkPValueInput(input) !== null) {
    return null;
  }
  const { statistic, value, tail } = input;
  if (statistic === "z") {
    const lower = normalCdf(value);
    const upper = normalUpper(value);
    return {
      p: pick(tail, lower, upper),
      distribution: "z",
      testValue: value,
      lower,
      upper,
    };
  }
  if (statistic === "t" || statistic === "r") {
    const df =
      statistic === "r" ? (input.n as number) - 2 : (input.df as number);
    const t = statistic === "r" ? rToT(value, input.n as number) : value;
    const lower = studentTCdf(t, df);
    const upper = studentTUpper(t, df);
    return {
      p: pick(tail, lower, upper),
      distribution: "t",
      testValue: t,
      df,
      lower,
      upper,
    };
  }
  if (statistic === "chi2") {
    const df = input.df as number;
    const lower = chiSquareCdf(value, df);
    const upper = chiSquareUpper(value, df);
    return {
      p: pick(tail, lower, upper),
      distribution: "chi2",
      testValue: value,
      df,
      lower,
      upper,
    };
  }
  const df1 = input.df1 as number;
  const df2 = input.df2 as number;
  const lower = fCdf(value, df1, df2);
  const upper = fUpper(value, df1, df2);
  return {
    p: pick(tail, lower, upper),
    distribution: "f",
    testValue: value,
    df1,
    df2,
    lower,
    upper,
  };
}

/**
 * The x-axis range a sketch of the distribution should show.
 *
 * For χ² and F the right end is the 99.5th percentile, but never more than eight
 * medians: F with one denominator df has a tail so heavy that its 99.5th percentile is
 * past 16,000, and a frame that wide squeezes the whole curve against the axis. A
 * statistic beyond twice that width is drawn at the edge instead.
 */
export function sketchRange(result: PValueResult): [number, number] {
  const x = result.testValue;
  if (result.distribution === "z" || result.distribution === "t") {
    const half = Math.min(6, Math.max(4, Math.abs(x) * 1.15));
    return [-half, half];
  }
  const quantile = quantileOf(result);
  const base = Math.min(quantile(0.995), 8 * quantile(0.5));
  const high = Math.min(Math.max(x * 1.15, base), 2 * base);
  // A χ² with many df sits far from zero; start the frame where the curve begins.
  const low =
    result.distribution === "chi2" && (result.df as number) >= 20
      ? quantile(0.000_01)
      : 0;
  return [low, high];
}

function quantileOf(result: PValueResult): (p: number) => number {
  if (result.distribution === "chi2") {
    return (p) => chiSquareQuantile(p, result.df as number);
  }
  return (p) => fQuantile(p, result.df1 as number, result.df2 as number);
}

export function sketchPdf(result: PValueResult): (x: number) => number {
  if (result.distribution === "z") {
    return (x) => normalPdf(x);
  }
  if (result.distribution === "t") {
    return (x) => studentTPdf(x, result.df as number);
  }
  if (result.distribution === "chi2") {
    return (x) => chiSquarePdf(x, result.df as number);
  }
  return (x) => fPdf(x, result.df1 as number, result.df2 as number);
}

/** The regions whose area is the p-value. */
export function shadedRegions(
  result: PValueResult,
  tail: Tail
): [number, number][] {
  const x = result.testValue;
  const inf = Number.POSITIVE_INFINITY;
  if (tail === "left") {
    return [[-inf, x]];
  }
  if (tail === "right") {
    return [[x, inf]];
  }
  if (result.distribution === "z" || result.distribution === "t") {
    const a = Math.abs(x);
    return [
      [-inf, -a],
      [a, inf],
    ];
  }
  // χ² and F are not symmetric: the two-tailed p doubles the smaller tail, so shade
  // that tail and its mirror-probability point in the other one.
  const quantile = quantileOf(result);
  if (result.lower <= result.upper) {
    return [
      [0, x],
      [quantile(1 - result.lower), inf],
    ];
  }
  return [
    [0, quantile(result.upper)],
    [x, inf],
  ];
}

/**
 * The statistic as APA 7 reports it: `t(24) = 2.31, p = .030`, `r(28) = .45, p = .013`.
 * Symbols only — the result line is notation, identical in every language.
 */
export function apaReport(
  input: PValueInput,
  result: PValueResult,
  fmt: NumberFormatter
): { statistic: string; plain: string; html: string } {
  const p = apaPText(result.p, fmt);
  const pHtml = p.replace("p ", "<i>p</i> ");
  const value = fixed(input.value, 2, fmt, input.statistic !== "r");
  const [plainHead, htmlHead] = statisticHead(input.statistic, result, fmt);
  const statistic = `${plainHead} = ${value}`;
  return {
    statistic,
    plain: `${statistic}, ${p}`,
    html: `${htmlHead} = ${value}, ${pHtml}`,
  };
}

/** The symbol and its df in parentheses: plain text and HTML (italic, superscript). */
function statisticHead(
  statistic: PStatistic,
  result: PValueResult,
  fmt: NumberFormatter
): [string, string] {
  if (statistic === "z") {
    return ["z", "<i>z</i>"];
  }
  if (statistic === "f") {
    const dfs = `(${formatDf(result.df1 as number, fmt)}, ${formatDf(result.df2 as number, fmt)})`;
    return [`F${dfs}`, `<i>F</i>${dfs}`];
  }
  const df = `(${formatDf(result.df as number, fmt)})`;
  if (statistic === "chi2") {
    return [`χ²${df}`, `χ<sup>2</sup>${df}`];
  }
  return [`${statistic}${df}`, `<i>${statistic}</i>${df}`];
}
